import classnames from 'classnames';
import React, { FC, memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { DragType } from '../../../../constants/DragType';
import { ThumbnailBackground } from '../../../../constants/ThumbnailBackground';
import { WixMediaRoot } from '../../../../constants/WixMediaRoot';
import { stopPropagation } from '../../../../utils/dom/domEvents';
import { ActionClickOrigin } from '../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { useTrackPreviewActions } from '../../../../utils/hooks/bi/useTrackPreviewActions';
import {
  ClickOrigin,
  PreviewTarget,
} from '../../../../utils/hooks/bi/useTrackPreviewActions/useTrackPreviewActions.types';
import { useGetItemPrice } from '../../../../utils/hooks/useGetItemPrice';
import { isItemRenamable, isDividersCategory } from '../../../../utils/media-manager-sdk/items';
import { isMediaFromWix } from '../../../../utils/media-manager-sdk/sources';
import { isReady } from '../../../../utils/media-manager-sdk/transcoding';
import { DragSource, Input, ItemActionsRow, TextButton } from '../../../common';
import { ActionButton } from '../../../common/ItemActionsRow/ItemActionsRow.types';
import { GenericItem } from '../../Generic/GenericItem';
import { IFile } from '../../types';
import baseItemStyles from '../GalleryItemBase.scss';
import { ItemName } from '../ItemName';
import fileStyles from './GalleryFile.scss';
import { GalleryFileControls } from './GalleryFileControls';
import { GalleryFileThumbnail } from './GalleryFileThumbnail';
import galleryFileThumbnailStyles from './GalleryFileThumbnail/GalleryFileThumbnail.scss';

const actionButtons = [ActionButton.CropAndEdit, ActionButton.More, ActionButton.AddToBoard];

const GalleryFileComponent: FC<IFile> = ({
  item,
  className,
  selected,
  isEditing,
  isCut,
  isSecure,
  onClick,
  onDoubleClick,
  onContextMenu,
  onRenameCancel,
  onRenameComplete,
  onNameChange,
  beginDrag,
  canDrag,
  selectedItems,
  publicMediaCurrentCategory,
  openExpandedView,
  buyItem,
  onRenameStart,
  currentFolder,
  onRemoveItemsFromBoard,
  source,
}) => {
  const { getItemPrice } = useGetItemPrice();

  const price = getItemPrice(item);

  const handleBuyClick = useCallback(() => buyItem(item), [buyItem, item]);

  const handleNameClick = useCallback(
    (event) => {
      if (selected && isItemRenamable(item)) {
        onRenameStart(item.id);
        stopPropagation(event);
      }
    },
    [selected, item, onRenameStart],
  );

  const isItemReady = isReady(item);
  const thumbnailBackground =
    isMediaFromWix(item.source) &&
    publicMediaCurrentCategory &&
    publicMediaCurrentCategory.parentId === WixMediaRoot.SocialIcons
      ? ThumbnailBackground.LightBlue
      : ThumbnailBackground.Transparent;

  const { trackPreviewActions } = useTrackPreviewActions();

  const openExpandedViewAndTrack = useCallback(() => {
    trackPreviewActions(
      {
        clickOrigin: ClickOrigin.VideoPlay,
        target: PreviewTarget.Opened,
      },
      item,
    );
    openExpandedView();
  }, [trackPreviewActions, openExpandedView, item]);

  const WIDE_WIDTH = 300;
  const NORMAL_WIDTH = 150;
  const itemWidth = isDividersCategory(publicMediaCurrentCategory) ? WIDE_WIDTH : NORMAL_WIDTH;

  return (
    <GenericItem
      className={classnames(
        baseItemStyles.item,
        {
          [fileStyles.selected]: selected,
          [baseItemStyles.selected]: selected,
          [fileStyles.isCut]: isCut,
          [baseItemStyles.double]: isDividersCategory(publicMediaCurrentCategory),
        },
        className,
      )}
      item={item}
      selected={selected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onNameChange={onNameChange}
      onRenameComplete={onRenameComplete}
      onRenameCancel={onRenameCancel}
      dataHook="gallery-file"
      canDrag={canDrag}
      selectedItems={selectedItems}
    >
      {({ handleNameChange, handleRenameComplete, handleRenameCancel, openContextMenu, canDrag }) => (
        <DragSource item={item} beginDrag={beginDrag} type={DragType.File} disabled={!canDrag || isEditing}>
          <div>
            <div
              className={classnames({
                [galleryFileThumbnailStyles.selected]: selected,
              })}
            >
              <GalleryFileThumbnail
                thumbnailBackground={thumbnailBackground}
                className={fileStyles.thumbnail}
                file={item}
                width={itemWidth}
                height={150}
                selected={selected}
                controls={isItemReady && <GalleryFileControls file={item} onPlayClick={openExpandedViewAndTrack} />}
              >
                <ItemActionsRow
                  onThreeDotsClick={openContextMenu}
                  buttonClassName={fileStyles.actionsRowButton}
                  item={item}
                  buttons={actionButtons}
                  isSecure={isSecure}
                  currentFolder={currentFolder}
                  onRemoveItemsFromBoard={onRemoveItemsFromBoard}
                  source={source}
                  actionsClickOrigin={ActionClickOrigin.File}
                />
              </GalleryFileThumbnail>
            </div>

            {isEditing ? (
              <Input
                value={item.name}
                onChange={handleNameChange}
                onBlur={handleRenameComplete}
                onEnterPressed={handleRenameComplete}
                onEscapePressed={handleRenameCancel}
                autoFocus
                dataHook="gallery-file-name-edit-input"
              />
            ) : (
              <div className={baseItemStyles.name} data-hook="gallery-file-name">
                <ItemName onClick={handleNameClick}>{item.name}</ItemName>

                {price && (
                  <TextButton
                    className={baseItemStyles.priceWrapper}
                    as="div"
                    size="tiny"
                    weight="normal"
                    underline="none"
                    dataHook="gallery-file-purchase-button"
                    onClick={handleBuyClick}
                  >
                    <FormattedMessage id="shutterstock.item.buy" values={{ price }} />
                  </TextButton>
                )}
              </div>
            )}
          </div>
        </DragSource>
      )}
    </GenericItem>
  );
};

export const GalleryFile: FC<IFile> = memo<IFile>(GalleryFileComponent);
